import { getStates, slugify, getNextOccurrenceDate } from '@/lib/util';
import {
  getAllParks,
  getAllEvents,
  getParkByCode,
  getParkByFullName,
  getParkNameOptions,
  getParkCodesByState,
  getStatesAndParkCodes,
  getEventsByParkCode,
} from '@/lib/db-queries';

// Centralized function to filter out closure events
export function filterOutClosureEvents(events) {
  return events.filter((event) => {
    const title = event.title || '';
    const name = event.name || '';
    const description = event.description || '';

    // Filter out events with 'Closed' in the title or name
    if (
      title.toLowerCase().includes('closed') ||
      name.toLowerCase().includes('closed')
    ) {
      return false;
    }

    // Filter out New Year's Day events that indicate closure
    if (
      title.toLowerCase().includes('new year') &&
      (description.toLowerCase().includes('closed') ||
        description.toLowerCase().includes('will be closed') ||
        description.toLowerCase().includes('remains closed'))
    ) {
      return false;
    }

    // Filter out Christmas closure events
    if (
      title.toLowerCase().includes('christmas') &&
      (description.toLowerCase().includes('closed') ||
        description.toLowerCase().includes('will be closed'))
    ) {
      return false;
    }

    // Filter out other holiday closure events
    if (
      (title.toLowerCase().includes('holiday') ||
        title.toLowerCase().includes('independence day')) &&
      description.toLowerCase().includes('closed')
    ) {
      return false;
    }

    // Filter out general closure events
    if (
      description.toLowerCase().includes('closed all day') ||
      description.toLowerCase().includes('will be closed') ||
      description.toLowerCase().includes('is closed') ||
      description.toLowerCase().includes('remains closed')
    ) {
      return false;
    }

    return true;
  });
}

export async function getParkNameSelectOptions() {
  return await getParkNameOptions();
}

export async function getParkNames() {
  const parks = await getAllParks();
  return parks.map((park) => park.fullName);
}

export async function getParkFromParkCode(parkCode) {
  const park = await getParkByCode(parkCode);
  return park ? [park] : [];
}

export async function getParkFromFullName(parkFullName) {
  const park = await getParkByFullName(parkFullName);
  return park ? [park] : [];
}

export async function statesAndParkCodes() {
  return await getStatesAndParkCodes();
}

export function getStateAbbr(stateFullName) {
  const states = getStates();
  let statesWithLabel = states.filter((state) => state.label === stateFullName);
  return statesWithLabel[0].value;
}

export async function getAllParkCodesForState(stateAbbr) {
  return await getParkCodesByState(stateAbbr);
}

export async function getEventsByPark(park) {
  // Fetch events for this park from database
  const events = await getEventsByParkCode(park.parkCode);
  // Filter out closure events
  return filterOutClosureEvents(events);
}

export function getEventsAndSortByDate(events, ignoreClosed = false) {
  // Use UTC to ensure consistent server/client rendering
  const today = new Date();
  const todayUTC = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())
  );

  // Filter out past events using the new date logic
  const filteredEvents = events.filter((event) => {
    // Check if the event should be excluded based on "Closed" in title
    const isClosed =
      ignoreClosed && event.title?.toLowerCase().includes('closed');
    if (isClosed) return false;

    // Use the new date logic to determine if event is still relevant
    const nextOccurrence = getNextOccurrenceDate(event);
    return nextOccurrence !== null;
  });

  // Sort events with prioritization
  filteredEvents.sort((a, z) => {
    // Check if events are ongoing vs upcoming
    const aNextOccurrence = getNextOccurrenceDate(a);
    const zNextOccurrence = getNextOccurrenceDate(z);

    // Prioritize ongoing events over upcoming events
    if (aNextOccurrence?.type !== zNextOccurrence?.type) {
      return aNextOccurrence?.type === 'ongoing' ? -1 : 1;
    }

    // For ongoing events, sort by end date (longer events first)
    if (
      aNextOccurrence?.type === 'ongoing' &&
      zNextOccurrence?.type === 'ongoing'
    ) {
      return (
        new Date(aNextOccurrence.endDate) - new Date(zNextOccurrence.endDate)
      );
    }

    // For upcoming events, sort by start date
    if (
      aNextOccurrence?.type === 'upcoming' &&
      zNextOccurrence?.type === 'upcoming'
    ) {
      return new Date(aNextOccurrence.date) - new Date(zNextOccurrence.date);
    }

    return 0;
  });

  return filteredEvents;
}
